/**
 * NOTIF-012 — the real-time link: Laravel Reverb over the Pusher protocol (laravel-echo +
 * pusher-js). The API says how to connect inside the unread summary (`realtime` — no build
 * argument, the switch in Admin → App Settings decides at run time); the link subscribes to
 * the person's own private channel and reports two things to the store: a push ("your
 * notifications changed" → the same `refresh()` the poll timer runs) and whether the socket
 * is up (→ how often to poll). Echo and pusher-js are loaded on first use only.
 */
import apiService from '@/services/api'
import type { RealtimeConfig } from '@/services/notificationsApi'

/** The event name as the API broadcasts it (`.`: no namespace, see NotificationsChanged::broadcastAs). */
export const REALTIME_EVENT = '.notifications.changed'

/** The API's private-channel authorization (Bearer token, impersonation — through the usual client). */
export const REALTIME_AUTH_PATH = '/v1/broadcasting/auth'

/** What a link is opened with — the summary's block, complete and checked. */
export interface RealtimeLinkConfig {
  key: string
  host: string
  port: number
  tls: boolean
  /** `users.{uuid}` — the person's own channel, named by the server. */
  channel: string
}

/**
 * The summary's `realtime` block as a link config, or null when the browser should not (or
 * cannot) connect: switched off, or a field missing. The scheme decides TLS; a missing port
 * falls back to the scheme's default.
 */
export function realtimeLinkConfig(
  realtime: RealtimeConfig | null | undefined
): RealtimeLinkConfig | null {
  if (!realtime || realtime.enabled !== true) return null
  const { key, host, channel } = realtime
  if (typeof key !== 'string' || key === '') return null
  if (typeof host !== 'string' || host === '') return null
  if (typeof channel !== 'string' || channel === '') return null
  const tls = realtime.scheme !== 'http'
  const port =
    typeof realtime.port === 'number' && Number.isInteger(realtime.port) && realtime.port > 0
      ? realtime.port
      : tls
        ? 443
        : 80
  return { key, host, port, tls, channel }
}

/** Same server, same channel → an open link stays as it is. */
export function sameLinkConfig(
  a: RealtimeLinkConfig | null,
  b: RealtimeLinkConfig | null
): boolean {
  if (a === null || b === null) return a === b
  return (
    a.key === b.key &&
    a.host === b.host &&
    a.port === b.port &&
    a.tls === b.tls &&
    a.channel === b.channel
  )
}

export interface RealtimeHandlers {
  /** "Your notifications changed" — refresh. */
  onPush: () => void
  /** The socket is up (true) or not (false): the store polls accordingly and refreshes on (re)connect. */
  onState: (connected: boolean) => void
}

export interface RealtimeLink {
  readonly config: RealtimeLinkConfig
  /** Closes the socket; nothing is reported afterwards. */
  disconnect(): void
}

export type RealtimeLinkFactory = (
  config: RealtimeLinkConfig,
  handlers: RealtimeHandlers
) => RealtimeLink

/** What Reverb asks the browser to prove: a signature our API makes for the person's own channel. */
interface ChannelAuthParams {
  socketId: string
  channelName: string
}
type ChannelAuthCallback = (error: Error | null, authData: { auth: string } | null) => void

/** Authorizes a private channel through our API client, so the Bearer token, impersonation and the 401 rule apply as everywhere. */
export function authorizeChannel(channelName: string, socketId: string): Promise<{ auth: string }> {
  return apiService.post<{ auth: string }>(REALTIME_AUTH_PATH, {
    socket_id: socketId,
    channel_name: channelName
  })
}

/** The bits of an Echo instance the link uses (so the dynamic import needs no type gymnastics). */
interface EchoInstance {
  private(channel: string): { listen(event: string, callback: () => void): unknown }
  connector: {
    onConnectionChange(callback: (status: string) => void): () => void
  }
  connectionStatus(): string
  disconnect(): void
}

/**
 * The production link: Echo with the `reverb` broadcaster, WebSockets only (no HTTP
 * fallbacks — the cluster's ingress speaks WebSocket), channel authorization through our
 * API client. Reports nothing once disconnected, even when the modules were still loading.
 */
export const createEchoLink: RealtimeLinkFactory = (config, handlers) => {
  let open = true
  let echo: EchoInstance | null = null
  let unbindState: (() => void) | null = null
  const push = (): void => {
    if (open) handlers.onPush()
  }
  const state = (connected: boolean): void => {
    if (open) handlers.onState(connected)
  }

  void (async () => {
    const [{ default: Echo }, { default: Pusher }] = await Promise.all([
      import('laravel-echo'),
      import('pusher-js')
    ])
    if (!open) return
    const instance = new Echo<'reverb'>({
      broadcaster: 'reverb',
      Pusher,
      key: config.key,
      wsHost: config.host,
      wsPort: config.port,
      wssPort: config.port,
      forceTLS: config.tls,
      enabledTransports: ['ws', 'wss'],
      withoutInterceptors: true,
      channelAuthorization: {
        customHandler: (
          { socketId, channelName }: ChannelAuthParams,
          callback: ChannelAuthCallback
        ) => {
          authorizeChannel(channelName, socketId).then(
            (data) => callback(null, data),
            (error: unknown) =>
              callback(
                error instanceof Error ? error : new Error('Channel authorization failed'),
                null
              )
          )
        }
      }
    }) as unknown as EchoInstance
    echo = instance
    instance.private(config.channel).listen(REALTIME_EVENT, push)
    unbindState = instance.connector.onConnectionChange((status) => state(status === 'connected'))
    state(instance.connectionStatus() === 'connected')
  })().catch(() => {
    // The modules did not load or the socket could not be set up: the store keeps polling.
    state(false)
  })

  return {
    config,
    disconnect(): void {
      open = false
      unbindState?.()
      unbindState = null
      echo?.disconnect()
      echo = null
    }
  }
}
